
import React from 'react';
import { Camera, X, Download, Play } from 'lucide-react';
import Button from '../Button';
import { Product, Category } from '../../types';

interface ProductDetailModalProps {
    product: Product;
    categories: Category[];
    onClose: () => void;
    onNavigateToStudio: () => void;
    onDelete: () => void;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({ product, categories, onClose, onNavigateToStudio, onDelete }) => {
  const handleDownload = async (url: string, isVideo: boolean) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `lumina-asset-${Date.now()}.${isVideo ? 'mp4' : 'png'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
        console.error("Download failed", e);
        window.open(url, '_blank');
    }
  };

  const isVideo = (url: string, type?: string) => {
     return type === 'video' || url.includes('.mp4') || url.includes('video/');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-stone-900/40 backdrop-blur-md transition-opacity" onClick={onClose} />
      <div className="relative bg-white rounded-[2.5rem] w-full max-w-6xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        
        <div className="p-8 border-b border-stone-100 flex justify-between items-center bg-white z-10 sticky top-0">
           <div>
             <h2 className="text-3xl font-serif font-bold text-stone-900 mb-1">{product.name}</h2>
             <p className="text-stone-500 font-medium text-sm">{product.images.length} assets available</p>
           </div>
           <div className="flex items-center gap-4">
             <Button variant="danger" size="sm" onClick={onDelete} className="bg-red-50 text-red-600 hover:bg-red-100 border-transparent shadow-none">
               Delete Product
             </Button>
             <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors"><X className="w-6 h-6 text-stone-400 hover:text-stone-900" /></button>
           </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
           <div className="w-full md:w-96 border-r border-stone-100 p-8 overflow-y-auto bg-white custom-scrollbar flex-shrink-0">
              <div className="mb-10">
                <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-4">Description</h4>
                <p className="text-stone-700 leading-loose text-sm">{product.description || "No description provided."}</p>
              </div>

              <div className="mb-10">
                 <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-4">Categories</h4>
                 <div className="flex flex-wrap gap-2">
                    {product.categoryIds.map((id: string) => {
                      const cat = categories.find((c: any) => c.id === id);
                      return cat ? <span key={id} className="text-[10px] font-bold uppercase tracking-wide bg-stone-100 text-stone-600 px-3 py-1.5 rounded-lg border border-stone-200">{cat.name}</span> : null;
                    })}
                 </div>
              </div>

              <div className="mb-10">
                 <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-4">Tags</h4>
                 <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag: string) => (
                      <span key={tag} className="text-xs border border-stone-200 px-3 py-1 rounded-full text-stone-500 font-medium">#{tag}</span>
                    ))}
                 </div>
              </div>

              <div className="pt-6 mt-auto border-t border-stone-100">
                <Button className="w-full shadow-xl shadow-stone-900/10 py-4" size="lg" onClick={onNavigateToStudio}>
                    <Camera className="w-5 h-5 mr-2" />
                    Open Studio Session
                </Button>
              </div>
           </div>

           <div className="flex-1 p-8 overflow-y-auto bg-stone-50/50 custom-scrollbar">
             <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
               {product.images.map((img: any) => {
                 const isVid = isVideo(img.url, img.type);
                 return (
                    <div key={img.id} className="group relative bg-white rounded-2xl p-2.5 shadow-sm border border-stone-100 hover:shadow-xl transition-all duration-300">
                        <div className="aspect-[3/4] rounded-xl overflow-hidden mb-3 bg-stone-50 cursor-pointer relative">
                        {isVid ? (
                            <video 
                                src={img.url} 
                                className="w-full h-full object-cover" 
                                controls 
                                playsInline 
                            />
                        ) : (
                            <>
                                <img src={img.url} alt={img.type} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" onClick={() => window.open(img.url, '_blank')} />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none"></div>
                            </>
                        )}
                        </div>
                        
                        <div className="flex justify-between items-center px-1">
                        <span className={`text-[10px] uppercase font-bold tracking-widest ${
                            img.type === 'original' ? 'text-stone-900' : 'text-stone-500'
                        }`}>
                            {img.type}
                        </span>
                        {img.style && <span className="text-[9px] bg-stone-100 px-2 py-1 rounded-md text-stone-500 font-bold uppercase">{img.style.split('/')[0]}</span>}
                        </div>
                        
                        <div className="absolute top-4 right-4">
                            <Button 
                                variant="glass" 
                                size="icon" 
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(img.url, isVid);
                                }} 
                                className="rounded-full shadow-md bg-white/80 backdrop-blur-md border border-white/50 hover:bg-white"
                            >
                                <Download className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                 );
               })}
             </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
